import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast, { Toaster } from "react-hot-toast";
import MUIDataTable from 'mui-datatables';
import CircularProgress from "@mui/material/CircularProgress";

const CategoryServiceTimeStatistics = (props) => {

    const baseUrl = localStorage.getItem('API_URL');
    const token = localStorage.getItem('user_token');

    const [categoryData, setCategoryData] = useState([]);
    const [isLoading, setLoading] = useState(false);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Helper function to convert HH:mm:ss to seconds
    const timeToSeconds = (timeString) => {
        if (!timeString || timeString === '00:00:00') return 0;
        const parts = timeString.split(':');
        if (parts.length !== 3) return 0;
        return parseInt(parts[0]) * 3600 + parseInt(parts[1]) * 60 + parseInt(parts[2]);
    };

    // Helper function to format number with thousand separators
    const formatNumber = (num) => {
        if (num === null || num === undefined) return '0';
        return parseInt(num).toLocaleString('en-US');
    };

    // Helper function to calculate percentage difference
    const calculatePercentageDiff = (avgTime, targetTime) => {
        const avgSeconds = timeToSeconds(avgTime);
        const targetSeconds = timeToSeconds(targetTime);
        if (targetSeconds === 0) return null;
        return ((avgSeconds - targetSeconds) / targetSeconds) * 100;
    };

    // Helper function to get row background color based on comparison
    const getRowBackgroundColor = (avgTime, targetTime) => {
        const percentageDiff = calculatePercentageDiff(avgTime, targetTime);
        if (percentageDiff === null) return 'transparent';
        if (percentageDiff <= 0) return 'rgba(76, 175, 80, 0.1)'; // Green
        if (percentageDiff <= 20) return 'rgba(255, 193, 7, 0.1)'; // Yellow
        return 'rgba(244, 67, 54, 0.1)'; // Red
    };

    const options = {
        tableBodyHeight: '50vh',
        filter: false,
        responsive: 'vertical',
        serverSide: false,
        download: false,
        viewColumns: false,
        jumpToPage: true,
        selectableRows: 'none',
        print: false,
        rowsPerPage: rowsPerPage,
        onChangeRowsPerPage: (n) => {
            setRowsPerPage(n)
        },
        rowsPerPageOptions: [10, 20, 30, 50, 100],
        setTableProps: () => {
            return {
                size: 'small',
            };
        },
        search: true,
        searchPlaceholder: 'Search categories...'
    };

    const columns = [
        {
            label: 'Category Name',
            name: 'category',
            options: {
                sort: true,
                filter: false
            }
        },
        {
            label: 'Token Count',
            name: 'tokenCount',
            options: {
                sort: true,
                filter: false,
                customBodyRender: (value) => {
                    return formatNumber(value);
                }
            }
        },
        {
            label: 'Average Service Time',
            name: 'avgServiceTime',
            options: {
                sort: true,
                filter: false,
                customBodyRender: (value) => {
                    return <strong>{value || '00:00:00'}</strong>;
                }
            }
        },
        {
            label: 'Max Service Time',
            name: 'maxServiceTime',
            options: {
                sort: true,
                filter: false
            }
        },
        {
            label: 'Min Service Time',
            name: 'minServiceTime',
            options: {
                sort: true,
                filter: false
            }
        },
        {
            label: 'Target Service Time',
            name: 'targetServiceTime',
            options: {
                sort: true,
                filter: false
            }
        }
    ];

    // Fetch category service time statistics
    const getCategoryServiceTimeStatistics = () => {
        setLoading(true);
        axios({
            method: 'GET',
            url: `${baseUrl}/ticket/category-service-time-statistics/branch/${props.selectedBranchId}`,
            headers: {
                Authorization: token
            }
        }).then((res) => {
            console.log('getCategoryServiceTimeStatistics success', res.data);
            if (res.data && (res.data.status === 'ok' || res.data.message === 'success') && res.data.data) {
                // Sort by category name alphabetically
                const sortedData = [...res.data.data].sort((a, b) => 
                    a.category.localeCompare(b.category)
                );
                setCategoryData(sortedData);
            } else {
                setCategoryData([]);
            }
            setLoading(false);
        }).catch((error) => {
            console.log('getCategoryServiceTimeStatistics error');
            console.log(error);
            setCategoryData([]);
            setLoading(false);
            
            if (error.response !== undefined) {
                toast.remove();
                toast.error(error.response.data.message || 'Failed to fetch category service time statistics', {
                    position: 'bottom-center',
                });
            } else {
                toast.remove();
                toast.error('Something went wrong', {
                    position: 'bottom-center',
                });
            }
        });
    };

    useEffect(() => {
        if (props.showCategoryServiceTimeStatistics && props.selectedBranchId) {
            getCategoryServiceTimeStatistics();
        }
    }, [props.showCategoryServiceTimeStatistics, props.selectedBranchId]);

    // Prepare table data
    const tableData = categoryData.map((item) => [
        item.category || 'N/A',
        item.tokenCount || 0,
        item.avgServiceTime || '00:00:00',
        item.maxServiceTime || '00:00:00',
        item.minServiceTime || '00:00:00',
        item.targetServiceTime || '00:00:00'
    ]);

    return (
        <>
            {props.showCategoryServiceTimeStatistics && (
                <div className={`modal fade d-block show modal-container`} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-xl" style={{ marginTop: '3%' }}>
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    Category Service Time Statistics
                                    {props.avgServiceTime && (
                                        <span className="ml-2" style={{ fontWeight: 'normal', fontSize: '0.9em', color: '#666' }}>
                                            (Avg: {props.avgServiceTime})
                                        </span>
                                    )}
                                </h5>
                                <button
                                    type="button"
                                    className="close"
                                    data-dismiss="modal"
                                    aria-label="Close"
                                    onClick={() => {
                                        props.setShowCategoryServiceTimeStatistics(false);
                                        props.setSticky(false);
                                    }}
                                >
                                    <span aria-hidden="true">×</span>
                                </button>
                            </div>

                            <div className="modal-body">
                                {isLoading ? (
                                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
                                        <CircularProgress />
                                    </div>
                                ) : categoryData.length > 0 ? (
                                    <MUIDataTable
                                        title=""
                                        data={tableData}
                                        columns={columns}
                                        options={{
                                            ...options,
                                            setRowProps: (row, dataIndex, rowIndex) => {
                                                const item = categoryData[rowIndex];
                                                const avgTime = item.avgServiceTime || '00:00:00';
                                                const targetTime = item.targetServiceTime || '00:00:00';
                                                const percentageDiff = calculatePercentageDiff(avgTime, targetTime);
                                                
                                                let title = '';
                                                if (percentageDiff !== null) {
                                                    if (percentageDiff > 0) {
                                                        title = `Average is ${percentageDiff.toFixed(1)}% above target`;
                                                    } else if (percentageDiff < 0) {
                                                        title = `Average is ${Math.abs(percentageDiff).toFixed(1)}% below target`;
                                                    } else {
                                                        title = 'Average equals target';
                                                    }
                                                }
                                                
                                                return {
                                                    style: {
                                                        backgroundColor: getRowBackgroundColor(avgTime, targetTime)
                                                    },
                                                    title: title
                                                };
                                            }
                                        }}
                                    />
                                ) : (
                                    <div style={{ textAlign: 'center', padding: '40px' }}>
                                        <p>No data available</p>
                                    </div>
                                )}
                            </div>

                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => {
                                        props.setShowCategoryServiceTimeStatistics(false);
                                        props.setSticky(false);
                                    }}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <Toaster />
        </>
    );
};

export default CategoryServiceTimeStatistics;

